import React, { useState } from 'react';
import { User, Role } from '../../../types/user';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  onSubmit: (data: Partial<User>) => void;
  initialData?: Partial<User>;
  roles: Record<string, Role>;
  isLoading?: boolean;
  isEditing?: boolean;
};

export const UserForm = ({
  onSubmit,
  initialData = {},
  roles,
  isLoading,
  isEditing,
}: Props) => {
  const { translations, direction } = useLanguage();
  
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    email: initialData.email || '',
    password: '',
    gender: initialData.gender || 'male' as const,
    roleId: initialData.roleId || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = translations?.team?.form?.validation?.required || 'Name is required';
    }

    if (!formData.roleId) {
      newErrors.roleId = translations?.team?.form?.validation?.required || 'Role is required';
    }

    if (!isEditing) {
      if (!formData.email.trim()) {
        newErrors.email = translations?.team?.form?.validation?.required || 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = translations?.team?.form?.validation?.emailInvalid || 'Invalid email address';
      }

      if (!formData.password) {
        newErrors.password = translations?.team?.form?.validation?.required || 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = translations?.team?.form?.validation?.passwordLength || 'Password must be at least 6 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" dir={direction}>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {translations?.team?.form?.fields?.name || 'Name'}
          <span className="text-red-500 mr-1">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg text-${direction === 'rtl' ? 'right' : 'left'} focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name}</p>
        )}
      </div>

      {!isEditing && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.team?.form?.fields?.email || 'Email'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg text-${direction === 'rtl' ? 'right' : 'left'} focus:ring-2 focus:ring-primary-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.team?.form?.fields?.password || 'Password'}
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg text-${direction === 'rtl' ? 'right' : 'left'} focus:ring-2 focus:ring-primary-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              minLength={6}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{errors.password}</p>
            )}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.team?.form?.fields?.gender || 'Gender'}
          </label>
          <select
            value={formData.gender}
            onChange={(e) => setFormData({ ...formData, gender: e.target.value as 'male' | 'female' })}
            className={`w-full px-4 py-2 border rounded-lg text-${direction === 'rtl' ? 'right' : 'left'} focus:ring-2 focus:ring-primary-500`}
          >
            <option value="male">{translations?.team?.search?.filters?.male || 'Male'}</option>
            <option value="female">{translations?.team?.search?.filters?.female || 'Female'}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.team?.form?.fields?.role || 'Role'}
            <span className="text-red-500 mr-1">*</span>
          </label>
          <select
            value={formData.roleId}
            onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg text-${direction === 'rtl' ? 'right' : 'left'} focus:ring-2 focus:ring-primary-500 ${
              errors.roleId ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          >
            {Object.values(roles).map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          {errors.roleId && (
            <p className="mt-1 text-sm text-red-500">{errors.roleId}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="submit"
          disabled={isLoading}
          className={`px-6 py-2 rounded-lg text-white transition-colors ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          {isLoading ? translations?.team?.form?.buttons?.saving : translations?.team?.form?.buttons?.save}
        </button>
      </div>
    </form>
  );
};